/**
 * Safe localStorage wrapper.
 *
 * Handles JSON serialization, errors (e.g., private browsing mode),
 * and provides a single place to control what's persisted.
 *
 * SECURITY NOTE: Never store sensitive data (JWT tokens, passwords,
 * medical data) in localStorage. It's vulnerable to XSS attacks.
 * This is for UI preferences only.
 */

/**
 * Get a value from localStorage.
 * @param {string} key
 * @param {*} fallback - returned if key missing or read fails
 * @returns {*}
 */
export function storageGet(key, fallback = null) {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw);
  } catch (err) {
    console.warn(`storage.get(${key}) failed:`, err);
    return fallback;
  }
}

/**
 * Set a value in localStorage.
 * @param {string} key
 * @param {*} value - any JSON-serializable value
 * @returns {boolean} success
 */
export function storageSet(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (err) {
    console.warn(`storage.set(${key}) failed:`, err);
    return false;
  }
}

/**
 * Remove a value from localStorage.
 * @param {string} key
 */
export function storageRemove(key) {
  try {
    window.localStorage.removeItem(key);
  } catch (err) {
    console.warn(`storage.remove(${key}) failed:`, err);
  }
}

/**
 * Known storage keys (use these constants — never hardcode key strings).
 */
export const STORAGE_KEYS = Object.freeze({
  LANGUAGE: 'app.language',
  // Add more as needed — never store tokens here.
});
